import *  as React from 'react';
import { useState, useEffect } from 'react';
import sidebarData from "../data/sidebarData";
import { AnimatePresence, motion } from "framer-motion"
import { useAtom, atom } from 'jotai'
import { imgMove, sceneAtom, currentUrl, selectedName } from "../data/AtomData"
import { useNavigate, useParams } from 'react-router-dom';

type SubMenu = {
	hasEdge?: boolean;
	hasPortal?: boolean;
	hasChannel?: boolean;
	hasExChannel?: boolean;
	text: string;
};

interface SidebarProps { }

const framerSidebarPanel = {
	initial: { x: '-100%' },
	animate: { x: 0 },
	exit: { x: '-100%' },
	transition: { duration: 0.2 },
}

const framerChildSidebarPanel = {
	initial: { x: '100%' },
	animate: { x: 0 },
	exit: { x: '100%' },
	leave: { x: '100%' },
	transition: { duration: 0.2 },
}

const extractImagePaths = (scene: { [index: string]: string | string[] }): string[] => {
	return Object.values(scene).flat();
};

const defaultConfig = {
	cabRange: 'cr1',
	cabCol: 'cc1',
	handleShape: 'hs1',
	backplate: 'bp1',
	worksurface: 'ws1',
};

const textToConfigKey: Record<string, keyof typeof defaultConfig> = {
	"Door Range": "cabRange",
	"Door Colour": "cabCol",
	"Handle Shape": "handleShape",
	"Backplate Finish": "backplate",
	"Worksurface": "worksurface",
};

const parseConfig = (configString: string) => {
	const config = { ...defaultConfig };

	const regex = /(cr\d{1,2})|(cc\d{1,2})|(hs\d{1,2})|(bp\d{1,2})|(ws\d{1,2})/g;
	const matches = configString.match(regex) || [];

	const configKeys = Object.keys(defaultConfig) as Array<keyof typeof defaultConfig>;

	matches.forEach((match, index) => {
		const key = configKeys[index];
		if (key) {
			config[key] = match;
		}
	});

	return config;
};

const useInitializeUrl = () => {
	const [, setCurrentUrl] = useAtom(currentUrl);

	useEffect(() => {
		const urlPath = window.location.pathname.slice(1); // Remove the leading slash
		const parsedUrl = parseConfig(urlPath);
		setCurrentUrl(parsedUrl);
	}, [setCurrentUrl]);
};


export const Sidebar: React.FC<SidebarProps> = (props: React.PropsWithChildren<SidebarProps>) => {
	const [selectedBlock, setSelectedBlock] = useState<number | null>(null);
	const [listSize, setListSize] = useState<string | undefined>("");
	const [itemNum, setItemNum] = useState(-1);
	const [childItemNum, setChildItemNum] = useState(-1);
	const [openSidebar, setOpenSidebar] = useState(false);
	const [openChildSidebar, setOpenChildSidebar] = useState(false);
	const [moveImg, setMoveImg] = useAtom(imgMove);
	const [allImgs, setAllImgs] = useAtom(sceneAtom);
	const [currentNames, setCurrentNames] = useAtom(selectedName);
	const [activeSection, setActiveSection] = useState(0);
	const [scene, setScene] = useAtom(sceneAtom);

	useInitializeUrl();

	const [isSmallScreen] = useState(window.innerWidth <= 700);

	useEffect(() => {
		if (isSmallScreen) {
			setItemNum(0);
			setSelectedBlock(0);
			setOpenSidebar(true);
			setMoveImg(!openSidebar);
			setOpenChildSidebar(false);
		}
	}, []);

	const handleClick = (ix: number) => {
		setItemNum(ix);
		if (selectedBlock === null) {
			!isSmallScreen && (setOpenSidebar(!openSidebar));
			setSelectedBlock(selectedBlock === ix ? selectedBlock : ix);
		}
		else if (selectedBlock === ix) {
			!isSmallScreen && (setOpenSidebar(!openSidebar));
		}
		else {
			setSelectedBlock(selectedBlock === ix ? selectedBlock : ix);
			!openSidebar && (!isSmallScreen && (setOpenSidebar(!openSidebar)));
			setActiveSection(selectedBlock);
		}
		setMoveImg(!openSidebar);
		setOpenChildSidebar(false);
	};

	const ChildMenuClick = (iq: number) => {
		setChildItemNum(iq);
		setOpenChildSidebar(!openChildSidebar);
	};

	const MenuBack = () => {
		setOpenChildSidebar(false);
	};

	//const ImageChange = (key: string, [bigImg, text, menuImg]: string[]) => {
	//	setAllImgs(prevState => ({
	//		...prevState,
	//		[key]: [bigImg, text, menuImg]
	//	}));
	//};

	const [isLoading, setIsLoading] = useState(false);

	const navigate = useNavigate();
	const { config } = useParams<{ config?: string }>();
	const initialConfig = config ? parseConfig(config) : defaultConfig;
	const [selectedConfig, setSelectedConfig] = React.useState(initialConfig);
	const [urlChange, setUrlChange] = useAtom(currentUrl);


	React.useEffect(() => {
		if (config) {
			const parsedConfig = parseConfig(config);
			setSelectedConfig(parsedConfig);
		}
	}, [config]);

	const updateConfigPart = (part: keyof typeof defaultConfig, value: string) => {
		const newConfig = { ...selectedConfig, [part]: value };

		const configString = [
			newConfig.cabRange || '',
			newConfig.cabCol || '',
			newConfig.handleShape || '',
			newConfig.backplate || '',
			newConfig.worksurface || '',
		].join('');

		setSelectedConfig(newConfig);

		setUrlChange(newConfig);

		navigate(`/${configString}`, { replace: true });
	};

	const handleItemClick = (part: string, newValue: string) => {
		const configKey = textToConfigKey[part];
		if (configKey) {
			updateConfigPart(configKey, newValue);
		} else {
			console.error(`No mapping found for part: ${part}`);
		}
	};

	const [imageP, setImageP] = useState<string[]>([]);
	const [imagePaths, setImagePaths] = useState<string[]>([]);

	useEffect(() => {
		const paths = extractImagePaths(allImgs);
		setImageP(paths);
	}, [allImgs]);

	useEffect(() => {
		if (imageP.length > 0) {
			const paths = [
				`Main_Images/Background_Layer.png`,
				`Main_Images/Cab_Fronts/${imageP[0]}/${imageP[2]}/Cab_Front--${imageP[0]}-${imageP[2]}-${imageP[1]}.png`,
				`Main_Images/Backplates/${imageP[0]}/${imageP[2]}/Backplate--${imageP[0]}-${imageP[2]}-${imageP[3]}.png`,
				`Main_Images/Worksurface/${imageP[4]}.png`,
				`Main_Images/Prop_Layer.png`,
			];
			setImagePaths(paths);
		}
	}, [imageP]);

	const [webproductNames, setWebproductNames] = useState<string[]>(currentNames);

	useEffect(() => {
		setWebproductNames(currentNames);
	}, [currentNames]);

	const handleImgDownload = async () => {
		setIsLoading(true);
		try {
			const response = await fetch('/api/image/upload-img', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ ImagePaths: imagePaths, ProductNames: webproductNames }),
			});

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			a.download = 'Outline_Image.png';
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading image:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const wholeUrl = window.location.href;

	const shareOnFacebook = () => {
		window.open(
			`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(wholeUrl)}`,
			'_blank'
		);
	};

	const shareOnLinkedIn = () => {
		window.open(
			`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(wholeUrl)}`,
			'_blank'
		);
	};

	const shareOnPinterest = () => {
		window.open(
			`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(wholeUrl)}`,
			'_blank'
		);
	};

	const shareViaEmail = () => {
		window.location.href = `mailto:?subject=Check out my new kitchen&body=Made with the Outline configurator: ${encodeURIComponent(wholeUrl)}`;
	};

	const selectedChild = sidebarData
		.find(item => item.subMenu.some(subItem => subItem.text === currentNames[0]))
		?.subMenu.find(subItem => subItem.text === currentNames[0]);

	const conditions: { property: keyof SubMenu; text: string }[] = [
		{ property: 'hasEdge', text: 'Edge' },
		{ property: 'hasPortal', text: 'Portal' },
		{ property: 'hasChannel', text: 'Channel' },
		{ property: 'hasExChannel', text: 'Extended Channel' }
	];

	const motionProps = openChildSidebar
		? {
			initial: { opacity: 0, x: -20 },
			animate: { opacity: 1, x: 0 },
			exit: { opacity: 0, x: 20 },
			transition: { duration: 0.4 },
		}
		: {
			initial: { opacity: 0, x: -10 },
			animate: { opacity: 1, x: 0 },
			exit: { opacity: 0, x: 10 },
			transition: { duration: 0.4 },
		};

	const mapIconImg: { [index: string]: number } = {
		"Door Colour": 1,
		"Handle Shape": 2,
		"Backplate Finish": 3,
		"Worksurface": 4,
	};

	return (<>
		<div className="sidebar">
			{sidebarData.map((block, ix) => (
					block.imgOnly ? (
					<div className={`sidebar-block img-only ${selectedBlock === ix && openSidebar ? " selected" : ""}`} key={block.id}>
						<a href="/" className="home-link">
							<img className={`${block.imgOnly ? '' : 'icon-image'}`} src={require(`../img/${block.imgCode}`)} alt={`${block.text} icon`} />
						</a>
					</div>
					) : (
							<div className={`sidebar-block ${selectedBlock === ix && openSidebar ? " selected" : ""}`}
								key={block.id} onClick={
									() => {
										if (block.id === "block-3") {
											if (openChildSidebar === true) {
												setOpenChildSidebar(false)
											}
											else {
												handleClick(ix);
												setListSize(block.listType);
											}
										}
										else {
											handleClick(ix);
											setListSize(block.listType);
										}
									}
								}
							>
								{block.fontAwesome ?
									<i className={`${block.imgCode}`}></i> :
									<img className={`${block.imgOnly ? '' : 'icon-image'}`} src={require(`../img/${block.imgCode}`)} alt={`${block.text} icon`} />}
								<p>{block.text}</p>
							</div>
					)
			))}
		</div>
		<AnimatePresence mode="wait" initial={false}>
			{openSidebar && (<>
				<motion.div
					className={`open-sidebar ${openChildSidebar ? "" : listSize}`}
					initial={isSmallScreen ? undefined : framerSidebarPanel.initial}
					animate={isSmallScreen ? undefined : framerSidebarPanel.animate}
					exit={isSmallScreen ? undefined : framerSidebarPanel.exit}
					transition={framerSidebarPanel.transition}
					aria-label="Sidebar"
					key="sidebar-open"
				>
					<div className="open-sidebar-column">
						{openChildSidebar ?
							<motion.div
								initial={{ x: '100%' }}
								animate={{ x: 0 }}
								exit={{ x: '100%' }}
								transition={{ duration: 0.1 }}
							>
								<div className="open-sidebar-child-top">
									<div className="back-button" onClick={() => MenuBack()}>
										<i className="fas fa-angle-left"></i>
									</div>
									<div className="top-text">
										<h3>{sidebarData[itemNum].subMenu[childItemNum].text}</h3>
										<h4>{scene[sidebarData[itemNum].subMenu[childItemNum].text][0].replace(/_/g, ' ')}</h4>
									</div>
								</div> <hr />

								<div className="open-sidebar-child-row">
									{sidebarData[itemNum].subMenu[childItemNum].childSubMenu?.map((childside, iq) => (<>
										{sidebarData[itemNum].subMenu[childItemNum].text === "Handle Shape" ? <>
											{selectedChild && (<>
												{selectedChild && conditions.map(({ property, text }) => (
													selectedChild[property] && childside.text === text && (
														<React.Fragment key={property}>
															<>
																<div className={`open-sidebar-single ${childside.text.replace(/\s+/g, "_") === allImgs[sidebarData[itemNum].subMenu[childItemNum].text][0] ? "selected" : ""}`}
																	key={childside.text}
																	onClick={() => {
																		if (childside.mainImg || childside.imagePart) {
																			//ImageChange(sidebarData[itemNum].subMenu[childItemNum].text, [childside.mainImg, childside.text, childside.sideImg])
																			const partToUpdate = sidebarData[itemNum].subMenu[childItemNum].text;
																			childside.webCode
																				? handleItemClick(partToUpdate, childside.webCode)
																				: console.error(`webCode is undefined for ${childside.text}`);
																			/*ImageChange(sidebarData[itemNum].text, [newside.mainImg, newside.text]);*/
																		}
																	}}>
																	<div className="open-sidebar-single-child">
																		<img src={require(`../img/Edit/${sidebarData[itemNum].subMenu[childItemNum].text.replace(/\s+/g, "_")}/${childside.imagePart}.jpg`)} alt={childside.text} />
																		<p>{childside.text}</p>
																	</div>
																</div>
															</>
														</React.Fragment>
													)
												))}
											</>)}
										</> :
											<>
												<div className={`open-sidebar-single ${childside.text.replace(/\s+/g, "_") === allImgs[sidebarData[itemNum].subMenu[childItemNum].text][0] ? "selected" : ""}`}
													key={childside.text}
													onClick={() => {
														if (childside.mainImg || childside.imagePart) {
															//ImageChange(sidebarData[itemNum].subMenu[childItemNum].text, [childside.mainImg, childside.text, childside.sideImg])
															const partToUpdate = sidebarData[itemNum].subMenu[childItemNum].text;
															childside.webCode
																? handleItemClick(partToUpdate, childside.webCode)
																: console.error(`webCode is undefined for ${childside.text}`);
															/*ImageChange(sidebarData[itemNum].text, [newside.mainImg, newside.text]);*/
														}
													}}>
													<div className="open-sidebar-single-child">
														{childside.imagePart && (
															<img src={require(`../img/Edit/${sidebarData[itemNum].subMenu[childItemNum].text.replace(/\s+/g, "_")}/${childside.imagePart}.jpg`)} alt={childside.text} />
														)}
														{childside.mainImg && (
															<img src={require(`../img/Edit/${sidebarData[itemNum].subMenu[childItemNum].text.replace(/\s+/g, "_")}/${childside.mainImg}.jpg`)} alt={childside.text} />
														)}
														<p>{childside.text}</p>
													</div>
												</div>
											</>}
									</>))}
								</div>
							</motion.div> :
							<>
								
								<h2>{sidebarData[itemNum].text}</h2>
								
								{sidebarData[itemNum].subMenu.map((newside, iq) => (<>
									{listSize === "large" && (
										<div className={`open-sidebar-single ${newside.text.replace(/\s+/g, "_") === allImgs[sidebarData[itemNum].text][0] ? "selected" : ""}`} key={newside.text} onClick={() => newside.childSubMenu != null ? ChildMenuClick(iq) : null}>
											<div className="open-sidebar-single-child"
												onClick={() => {
													const partToUpdate = sidebarData[itemNum].text;
													newside.webCode
														? handleItemClick(partToUpdate, newside.webCode)
														: console.error(`webCode is undefined for ${newside.text}`);
													/*ImageChange(sidebarData[itemNum].text, [newside.mainImg, newside.text]);*/
												}}
											>
												{newside.fontAwesome ?
													<i className={`${newside.imgCode}`}></i> :
													<img src={require(`../img/Edit/${newside.imgCode}.jpg`)} alt={newside.text} />}

												<p>{newside.text}</p>
											</div>
										</div>
									)}
									{listSize === "medium" && (<>
										{newside.text === "Handle Shape" ? 
											((scene["Door Range"][0] === "Porter") || (scene["Door Range"][0] === "Fitzroy") ? (
												<div className={`open-sidebar-single ${!newside.childSubMenu && (newside.text === allImgs[sidebarData[itemNum].text][0] ? "selected" : "")}`} key={newside.text} onClick={() => newside.childSubMenu != null ? ChildMenuClick(iq) : null}>
													<div className="open-sidebar-single-child">

														{Object.entries(mapIconImg).map(([key, index]) =>
															newside.text === key ? (
																<img key={key} alt={newside.text}
																	src={require(`../img/Edit/${key.replace(/\s+/g, "_")}/${currentNames[index].replace(/\s+/g, "_")}.jpg`)}
																/>) : null
														)}

														<div className="side-text">
															<h5>{newside.text}</h5>
															<p>{allImgs[newside.text][0].replace(/_/g, ' ')}</p>
														</div>

														<div className="arrow">
															<i className="fas fa-angle-right"></i>
														</div>
													</div>
												</div>
											) : <></>)
											
											: (<>
												<div className={`open-sidebar-single ${!newside.childSubMenu && (newside.text === allImgs[sidebarData[itemNum].text][0] ? "selected" : "")}`} key={newside.text} onClick={() => newside.childSubMenu != null ? ChildMenuClick(iq) : null}>
													<div className="open-sidebar-single-child">

														{Object.entries(mapIconImg).map(([key, index]) =>
															newside.text === key ? (
																<img key={key} alt={newside.text}
																	src={require(`../img/Edit/${key.replace(/\s+/g, "_")}/${currentNames[index].replace(/\s+/g, "_")}.jpg`)}
																/>) : null
														)}

														<div className="side-text">
															<h5>{newside.text}</h5>
															<p>{allImgs[newside.text][0].replace(/_/g, ' ')}</p>
														</div>

														<div className="arrow">
															<i className="fas fa-angle-right"></i>
														</div>
													</div>
												</div>
											</>)}
									</>)}
									{listSize === "small" && (<>
										<div className="open-sidebar-single smaller hoverable" key={newside.text} onClick={() => newside.childSubMenu != null ? ChildMenuClick(iq) : null}>
											<div className="open-sidebar-single-child"
												onClick={() => {
													if (newside.text === "Download Image") {
														handleImgDownload();
													}
												}}
											>
												{newside.fontAwesome ?
													<i className={`${newside.imgCode}`}></i> :
													<img src={require(`../img/Edit/${newside.imgCode}.jpg`)} alt={newside.text} />}

												<p>{newside.text}</p>
											</div>
										</div>
									</>)}									
								</>))}
								{sidebarData[itemNum].text === "Export" && (
									<>
										<div className="open-sidebar-single smaller hoverable" onClick={shareViaEmail}>
											<div className="open-sidebar-single-child">
												<i className="fas fa-envelope fa-lg fa-fw"></i>
												<p>Email</p>
											</div>
										</div>

										<div className="open-sidebar-single smaller hoverable" onClick={shareOnFacebook}>
											<div className="open-sidebar-single-child">
												<i className="fab fa-facebook-square fa-lg fa-fw"></i>
												<p>Facebook</p>
											</div>
										</div>

										<div className="open-sidebar-single smaller hoverable" onClick={shareOnPinterest}>
											<div className="open-sidebar-single-child">
												<i className="fab fa-pinterest-square fa-lg fa-fw"></i>
												<p>Pinterest</p>
											</div>
										</div>

										<div className="open-sidebar-single smaller hoverable" onClick={shareOnLinkedIn}>
											<div className="open-sidebar-single-child">
												<i className="fab fa-linkedin fa-lg fa-fw"></i>
												<p>LinkedIn</p>
											</div>
										</div>
									</>
								)}
							</>
						}
					</div>

					<div className="close-side" onClick={() => { handleClick(itemNum) }}>
						<i className="fas fa-times"></i>
					</div>

				</motion.div>
			</>)}
		</AnimatePresence>
		{isLoading && <div className="download-loading">
			<i className="fas fa-circle-notch fa-spin"></i>
		</div>
		}
	</>);
}